<template>
  <div class="dropdown">
    <Button
      class="dropdown__trigger"
      :class="{ 'is-open': isOpen }"
      :color="buttonColor"
      :appearance="buttonAppearance"
      :hover="buttonHover"
      :icon="icon"
      :icon-position="iconPosition"
      :disabled="disabled"
      @click="toggle"
      ref="triggerRef"
    >
      <span v-if="props.text && !props.value" class="dropdown__trigger-text">{{ props.text }}</span>

      <span v-if="props.text && props.value" class="dropdown__trigger-content">
        <span class="dropdown__trigger-text">{{ props.text }}</span>
        <span class="dropdown__trigger-value">{{ props.value }}</span>
      </span>
    </Button>

    <Transition name="dropdown-fade">
      <Teleport to="body">
        <div v-show="isOpen" class="dropdown__panel" :class="props.panelClass" :style="panelStyle" ref="panelRef" @click="close">
          <slot></slot>
        </div>
      </Teleport>
    </Transition>
  </div>
</template>

<script setup lang="ts">
import { nextTick, onBeforeUnmount, onMounted, ref } from "vue";
import Button from "../button/Button.vue";

const props = defineProps({
  text: { type: String, default: "" },
  value: { type: [String, Number], default: null },
  icon: { type: String, default: "" },
  iconPosition: { type: String as () => "left" | "right", default: "left" },
  buttonColor: { type: String as () => "green" | "blue" | "transparent", default: "transparent" },
  buttonAppearance: { type: String as () => "none" | "outline" | "solid", default: "solid" },
  buttonHover: { type: [String, Boolean], default: "opacity" },
  disabled: { type: Boolean, default: false },
  align: { type: String as () => "start" | "end", default: "start" },
  panelClass: { type: String, default: "" },
});

const isOpen = ref(false);
const panelStyle = ref<Record<string, string>>({});
const triggerRef = ref<InstanceType<typeof Button> | null>(null);
const panelRef = ref<HTMLElement | null>(null);

const EDGE_GAP = 8;
const OFFSET = 8;

function toggle() {
  if (props.disabled) return;
  isOpen.value = !isOpen.value;
  if (isOpen.value) {
    nextTick(() => {
      positionPanel();
    });
  }
}

function close() {
  isOpen.value = false;
}

function positionPanel() {
  if (!isOpen.value) return;
  const triggerEl = triggerRef.value?.$el as HTMLElement | undefined;
  const panelEl = panelRef.value;
  if (!triggerEl || !panelEl) return;

  const triggerRect = triggerEl.getBoundingClientRect();
  const panelRect = panelEl.getBoundingClientRect();
  const vw = document.documentElement.clientWidth;
  const vh = document.documentElement.clientHeight;

  const spaceBelow = vh - triggerRect.bottom - OFFSET - EDGE_GAP;
  const spaceAbove = triggerRect.top - OFFSET - EDGE_GAP;
  const openAbove = panelRect.height > spaceBelow && spaceAbove > spaceBelow;

  const maxPanelWidth = Math.max(0, vw - EDGE_GAP * 2);
  const desiredMinWidth = Math.min(Math.round(triggerRect.width), maxPanelWidth);
  const desiredWidth = Math.min(Math.max(panelRect.width, desiredMinWidth), maxPanelWidth);

  // Clamp in viewport coordinates (Teleported panel is rendered under <body>).
  const desiredLeft = props.align === "end" ? triggerRect.right - desiredWidth : triggerRect.left;
  const left = Math.min(Math.max(desiredLeft, EDGE_GAP), vw - EDGE_GAP - desiredWidth);

  const top = openAbove ? triggerRect.top - OFFSET - panelRect.height : triggerRect.bottom + OFFSET;
  const topClamped = Math.min(Math.max(top, EDGE_GAP), vh - EDGE_GAP - panelRect.height);

  const maxHeight = Math.max(80, openAbove ? spaceAbove : spaceBelow);

  panelStyle.value = {
    left: `${Math.round(left)}px`,
    top: `${Math.round(topClamped)}px`,
    maxHeight: `${Math.round(maxHeight)}px`,
    width: `${Math.round(desiredWidth)}px`,
  };
}

function handleDocumentClick(event: MouseEvent) {
  if (!isOpen.value) return;
  const target = event.target as Node | null;
  const triggerEl = triggerRef.value?.$el as HTMLElement | undefined;
  if (panelRef.value?.contains(target) || triggerEl?.contains(target)) return;
  close();
}

function handleScroll() {
  if (!isOpen.value) return;
  positionPanel();
}

onMounted(() => {
  document.addEventListener("mousedown", handleDocumentClick);
  window.addEventListener("resize", positionPanel);
  window.addEventListener("scroll", handleScroll, true);
});

onBeforeUnmount(() => {
  document.removeEventListener("mousedown", handleDocumentClick);
  window.removeEventListener("resize", positionPanel);
  window.removeEventListener("scroll", handleScroll, true);
});
</script>

<style scoped lang="scss">
.dropdown {
  display: inline-block;
  position: relative;
}

.dropdown__trigger-content {
  display: inline-flex;
  align-items: center;
  gap: 8px;
}

.dropdown__trigger-value {
  font-variant-numeric: tabular-nums;
  font-weight: 700;
}

.dropdown__trigger.is-open {
  opacity: 0.9;
}

.dropdown__panel {
  position: fixed;
  /* Выше .modal-overlay (9999): панель в Teleport на body, иначе меню под затемнением и «не кликается». */
  z-index: 10050;
  background: var(--popover);
  color: var(--popover-foreground);
  border-radius: 12px;
  padding: 8px 0;
  min-width: 180px;
  overflow-x: hidden;
  max-width: calc(100vw - 16px);
  // border: 1px solid color-mix(in oklch, var(--color-chrome-ring) 95%, transparent);
  border: 1px solid var(--color-popover-ring);
  box-shadow:
    0 10px 22px rgba(0, 0, 0, 0.35),
    0 2px 6px rgba(0, 0, 0, 0.25);
  overflow: auto;
}

.dropdown__panel :deep(a),
.dropdown__panel :deep(button),
.dropdown__panel :deep(div),
.dropdown__panel :deep(li) {
  color: inherit;
}

.dropdown-fade-enter-active,
.dropdown-fade-leave-active {
  transition:
    opacity 140ms ease,
    transform 140ms ease;
}

.dropdown-fade-enter-from,
.dropdown-fade-leave-to {
  opacity: 0;
  transform: translateY(-6px);
}
</style>
